import { PreviewAlertBox } from "components/Alert/PreviewAlert"
import { InfoIcon } from "components/SvgIcons"
import React, { useState } from "react"
import { colors } from "lib/theme"
import { Link } from "components/Link"
import { tagToRegion } from "lib/i18n"
import { useRouter } from "lib/router"
import { LocaleRadio } from "components/Preview/LocaleRadio"
import { useExperimentPreview } from "components/Experiments/ExperimentPreviewProvider/ExperimentPreviewProvider"
import { ExperimentVariantSelect } from "components/Preview/ExperimentVariantSelect"
import {
  RadioButtonGroup,
  RadioButton,
  RadioLabel,
} from "packages/ui-shared/forms/RadioButton"

import {
  hpContainer,
  noPageContainer,
  showContainer,
  radioButtons,
  alertContainer,
  warnings,
} from "./PreviewNotification.css"

type Props = {
  slug: string
  hpWarn?: boolean
  locale: "en-GB" | "en-US"
}

export const PreviewNotification: React.FC<Props> = ({
  slug,
  hpWarn,
  locale,
}) => {
  const router = useRouter()
  const relativeSlug = `${router?.asPath}`
  const thisLocale = tagToRegion(locale).toUpperCase()
  const [value, setValue] = useState("display")
  const allPageVariants = useExperimentPreview()

  return (
    <>
      {hpWarn && (
        <div className={hpContainer}>
          <h1>
            You are editing a homepage, be careful if you are not sure of the
            changes you're making.
          </h1>
        </div>
      )}
      {slug === "/no-page" ? (
        <div className={noPageContainer}>
          <h1>
            You have entered "no-page" or "no-article" as the slug for this
            entry in this locale - {thisLocale}.
            <br />
            This page will error on live, go back and change your slug field if
            you want this page in this locale to be live.
            <br />
            Click these links to check both locales
            <br />
            <a href={relativeSlug} target="_blank" rel="noreferrer">
              UK
            </a>{" "}
            |{" "}
            <a href={`/us${relativeSlug}`} target="_blank" rel="noreferrer">
              US
            </a>
          </h1>
        </div>
      ) : (
        <>
          <div className={showContainer}>
            <p>Show Preview details</p>
            <RadioButtonGroup removeSpaceBetween>
              <div className={radioButtons}>
                <RadioButton
                  name="showPreview"
                  value="display"
                  checked={value === "display"}
                  onChange={setValue}
                >
                  <RadioLabel label="Show" />
                </RadioButton>

                <RadioButton
                  name="hidePreview"
                  value="none"
                  checked={value === "none"}
                  onChange={setValue}
                >
                  <RadioLabel label="Hide" />
                </RadioButton>
              </div>
            </RadioButtonGroup>
          </div>
          <div className={alertContainer[value]}>
            <PreviewAlertBox />
            <div className={warnings}>
              <InfoIcon boxSize="2rem" color={colors.contentSecondary} />
              <div>
                <p>Do not share this page externally!</p>
                <p>
                  The live URL will be{" "}
                  <Link
                    variant="acuminSemiBold"
                    href={slug}
                    textDecor="underline"
                  >
                    {slug}
                  </Link>
                </p>
                <p>
                  If the live link doesn't work, the post/page isn't published
                  yet.
                </p>
              </div>
            </div>

            <div className={warnings}>
              <InfoIcon boxSize="2rem" color={colors.contentSecondary} />
              <div>
                <p>
                  Make sure you are checking the correct locale, this is the{" "}
                  <u>{thisLocale}</u>
                </p>
                <p>&nbsp;</p>
                <p>
                  <a href={relativeSlug} target="_blank" rel="noreferrer">
                    UK Preview
                  </a>{" "}
                  |{" "}
                  <a
                    href={`/us${relativeSlug}`}
                    target="_blank"
                    rel="noreferrer"
                  >
                    US Preview
                  </a>
                </p>
                <p>
                  If one of the previews doesn't work, check the slug field in
                  that entry, it may be that the slugs are different across
                  locales.
                </p>
                <p>&nbsp;</p>
                <p>
                  Note: An entry with the field "no-article" or "no-page" will
                  not be created.
                </p>
              </div>
            </div>
          </div>
          <LocaleRadio
            region={locale === "en-US" ? "us" : "uk"}
            slug={relativeSlug}
          />
          {allPageVariants?.length > 1 ? (
            <ExperimentVariantSelect variants={allPageVariants} />
          ) : null}
        </>
      )}
    </>
  )
}
